/**
 * Provides Maia 3 engine instances to the app.
 *
 * - `maia`: single shared instance (backward compat — pipeline, Play page)
 * - `pool`: parallel query pool (N workers for the Analyze tab)
 *
 * Mounted once at the App level. Workers are created eagerly on mount
 * and begin downloading the ONNX model immediately.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Maia3, type MaiaStatus, type MaiaAnalysis } from '../lib/maia/maia';

export interface MaiaPool {
  analyzeParallel(fen: string, elos: number[], topK?: number, playedUci?: string): Promise<(MaiaAnalysis | null)[]>;
  /** Analyze MANY positions at a single elo, spread round-robin across all workers (true N-way
   *  parallelism). For the win% rollout, which evaluates dozens of after-positions — analyzeParallel
   *  with a 1-elo array would pin them all to worker[0]. Returns results aligned to `fens`. */
  analyzeMany(fens: string[], elo: number, topK?: number): Promise<(MaiaAnalysis | null)[]>;
  lookupParallel(fen: string, elos: number[], uci: string): Promise<number[]>;
  /** ONE position at ONE elo, dispatched to the next worker round-robin. For callers that issue many
   *  independent single queries (the batch popularity enrichment: N moments × 5 elos). `analyzeParallel`
   *  with a 1-elo array would pin every one of them to worker[0] — which is exactly how enrichment ended
   *  up at {worker1: 57, worker2..6: 0} (2026-08-07). */
  analyzeOne(fen: string, elo: number, topK?: number, playedUci?: string): Promise<MaiaAnalysis | null>;
  readonly size: number;
}

interface MaiaEngineValue {
  maia: Maia3 | null;
  pool: MaiaPool | null;
  status: MaiaStatus;
  progress: number;
  error: string | null;
  retry: () => void;
  preload: () => void;
}

const MaiaEngineContext = createContext<MaiaEngineValue | null>(null);

const MODEL_URL =
  process.env.REACT_APP_MAIA_MODEL_URL || '/maia3/maia3_simplified.onnx';
const MODEL_VERSION = process.env.REACT_APP_MAIA_MODEL_VERSION || '3';

function getPoolSize(): number {
  if (typeof navigator === 'undefined') return 1;
  const cores = navigator.hardwareConcurrency || 4;
  const memory = 'deviceMemory' in navigator ? (navigator as { deviceMemory?: number }).deviceMemory ?? 4 : 4;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile) return Math.min(2, cores);
  if (memory >= 8 && cores >= 4) return 5;
  if (memory >= 4) return 3;
  return 2;
}

class MaiaPoolImpl implements MaiaPool {
  private workers: Maia3[] = [];
  private ready = false;
  private failed = false;
  private readyPromise: Promise<void>;

  /** `gate` (optional) is awaited before ANY pool worker warms up — the provider passes the single
   *  instance's preload, so the pool starts from a populated cache instead of racing it for the same
   *  43MB. Rejection is swallowed: if the single instance fails, the pool still tries on its own. */
  constructor(count: number, onStatus: (s: MaiaStatus) => void, onProgress: (p: number) => void, onError: (msg: string) => void, gate?: Promise<unknown>) {
    // Give each pooled worker a SLICE of the cores (cores/poolSize) instead of the ORT default
    // (~all cores). With N workers each grabbing every core, concurrent inferences oversubscribe the
    // CPU and thrash — so batching the win% rollout across workers gave no speedup. A per-worker
    // budget lets the N workers genuinely run in parallel. Floor of 1; leave headroom via +1 divisor.
    const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
    const threadsPerWorker = Math.max(1, Math.floor(cores / count));
    this.workers = Array.from({ length: count }, () =>
      new Maia3({
        modelUrl: MODEL_URL,
        modelVersion: MODEL_VERSION,
        numThreads: threadsPerWorker,
        onStatus,
        onProgress,
        onError,
      }),
    );
    // Warm the FIRST worker alone, then the rest together. Not a style choice — a Promise.all over all
    // N raced them: every worker checked IndexedDB before any had stored the blob, all missed, and all
    // downloaded the same 43MB model. With the pool (5) plus the single instance that was ~258MB per
    // cold load (measured 2026-08-07). Letting worker 0 finish first populates the cache, so workers
    // 1..N-1 hit it and start from disk. Costs nothing on a warm cache (worker 0 resolves immediately)
    // and the pool is warmed in the background, so the serialization isn't on any user's critical path.
    this.readyPromise = (async () => {
      // Wait for the single instance's download (the gate) before touching the network ourselves.
      if (gate) await gate.catch(() => {});
      const [first, ...rest] = this.workers;
      if (first) await first.preload();
      await Promise.all(rest.map(w => w.preload()));
    })().then(() => { this.ready = true; }).catch((err) => {
      this.failed = true;
      onError(err?.message || 'Pool failed to initialize');
    });
  }

  get size(): number {
    return this.workers.length;
  }

  async analyzeParallel(
    fen: string,
    elos: number[],
    topK = 10,
    playedUci?: string,
  ): Promise<(MaiaAnalysis | null)[]> {
    if (this.failed) return elos.map(() => null);
    await this.readyPromise;
    return Promise.all(
      elos.map((elo, i) => {
        const worker = this.workers[i % this.workers.length];
        return worker.analyze(fen, elo, elo, topK, playedUci).catch(() => null);
      }),
    );
  }

  async analyzeMany(fens: string[], elo: number, topK = 1): Promise<(MaiaAnalysis | null)[]> {
    if (this.failed) return fens.map(() => null);
    await this.readyPromise;
    // Round-robin positions across ALL workers (worker[i % pool]) so a batch of N after-positions
    // runs ~N/poolSize deep instead of all serializing on one worker.
    return Promise.all(
      fens.map((f, i) => {
        const worker = this.workers[i % this.workers.length];
        return worker.analyze(f, elo, elo, topK).catch(() => null);
      }),
    );
  }

  /** Rotating cursor for analyzeOne — module-free, per-pool state. */
  private rr = 0;

  async analyzeOne(fen: string, elo: number, topK = 10, playedUci?: string): Promise<MaiaAnalysis | null> {
    if (this.failed) return null;
    await this.readyPromise;
    const worker = this.workers[this.rr++ % this.workers.length];
    return worker.analyze(fen, elo, elo, topK, playedUci).catch(() => null);
  }

  async lookupParallel(fen: string, elos: number[], uci: string): Promise<number[]> {
    if (this.failed) return elos.map(() => 0);
    await this.readyPromise;
    const results = await Promise.all(
      elos.map((elo, i) => {
        const worker = this.workers[i % this.workers.length];
        return worker.analyze(fen, elo, elo, 1, undefined, [uci]).catch(() => null);
      }),
    );
    return results.map(a => Math.round((a?.lookupProbs?.[uci] ?? 0) * 100));
  }

  dispose(): void {
    for (const w of this.workers) w.dispose();
    this.workers = [];
  }
}

export function MaiaEngineProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<MaiaStatus>('loading');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [maia, setMaia] = useState<Maia3 | null>(null);
  const [pool, setPool] = useState<MaiaPoolImpl | null>(null);
  const instanceRef = useRef<Maia3 | null>(null);
  const poolRef = useRef<MaiaPoolImpl | null>(null);

  const ensureInstance = useCallback((): Maia3 | null => {
    if (instanceRef.current) return instanceRef.current;
    if (typeof Worker === 'undefined') return null;
    setStatus('loading');
    setProgress(0);
    setError(null);
    const next = new Maia3({
      modelUrl: MODEL_URL,
      modelVersion: MODEL_VERSION,
      onStatus: setStatus,
      onProgress: setProgress,
      onError: (msg) => setError(msg),
    });
    instanceRef.current = next;
    setMaia(next);
    return next;
  }, []);

  const ensurePool = useCallback((gate?: Promise<unknown>): MaiaPoolImpl | null => {
    if (poolRef.current) return poolRef.current;
    if (typeof Worker === 'undefined') return null;
    const count = getPoolSize();
    // The UI progress bar + status must track ONLY the single `instance` download. The pool's N
    // workers each fetch the same model and report their own progress/status; wiring those to the
    // shared UI state lets a fast (cache-served) pool worker stomp the instance's streaming
    // progress — the bar reads 0, then jumps to done, with no smooth fill. Give the pool no-op
    // status/progress; keep onError so a genuine pool failure still surfaces.
    const p = new MaiaPoolImpl(count, () => {}, () => {}, (msg) => setError(msg), gate);
    poolRef.current = p;
    setPool(p);
    return p;
  }, []);

  const preload = useCallback(() => {
    const instance = ensureInstance();
    // The pool waits on the single instance's warm-up so ONE worker fetches the 43MB model and the
    // other five load it from IndexedDB. Previously all six raced and all six downloaded.
    const gate = instance ? instance.preload().catch(() => {}) : undefined;
    ensurePool(gate);
  }, [ensureInstance, ensurePool]);

  const retry = useCallback(() => {
    if (instanceRef.current) { instanceRef.current.dispose(); instanceRef.current = null; }
    if (poolRef.current) { poolRef.current.dispose(); poolRef.current = null; }
    setMaia(null);
    setPool(null);
    preload();
  }, [preload]);

  // No eager preload here. The 43MB ONNX model is only needed by Review and Play, which call
  // preload() themselves on mount. Downloading it on app load made every page (Landing, Library,
  // Profile, Practice) pay for weights they never use. Singletons live for the page lifetime once
  // created; no cleanup (disposing on strict-mode remount throws "Maia3 disposed").

  const value = useMemo<MaiaEngineValue>(
    () => ({ maia, pool, status, progress, error, retry, preload }),
    [maia, pool, status, progress, error, retry, preload],
  );

  return (
    <MaiaEngineContext.Provider value={value}>
      {children}
    </MaiaEngineContext.Provider>
  );
}

export function useMaiaEngine(): MaiaEngineValue {
  const v = useContext(MaiaEngineContext);
  if (!v) throw new Error('useMaiaEngine must be used within MaiaEngineProvider');
  return v;
}
